import React, { useState, useEffect } from "react";
import { getLanguageValue } from "locales/i18n";
import { PAGE_TYPE } from "enums";
import { getSpecificDiaryStats, ISpecificDiaryStatsResult } from "api/student/v2/diary.api";

interface DiaryStatsPopupProps {
    diaryId: string;
    onClose: () => void;
}

const DiaryStatsPopup: React.FC<DiaryStatsPopupProps> = ({ diaryId, onClose }) => {
    const [statsData, setStatsData] = useState<ISpecificDiaryStatsResult | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchStats = async () => {
            try {
                setIsLoading(true);
                const response = await getSpecificDiaryStats(diaryId);
                setStatsData(response);
            } catch (err) {
                console.error("통계 데이터 로딩 실패:", err);
                setError("통계 데이터를 불러오는데 실패했습니다.");
            } finally {
                setIsLoading(false);
            }
        };

        if (diaryId) {
            fetchStats();
        }
    }, [diaryId]);

    // 로딩 상태
    if (isLoading) {
        return (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
                <div className="bg-gray-50 rounded-2xl shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
                    {/* 헤더 스켈레톤 */}
                    <div className="sticky top-0 z-10 bg-gray-50 border-b border-gray-200 shadow-sm">
                        <div className="flex items-center justify-between py-4 px-6">
                            <div>
                                <div className="h-6 bg-gray-300 rounded w-32 mb-2 animate-pulse"></div>
                                <div className="h-4 bg-gray-300 rounded w-48 animate-pulse"></div>
                            </div>
                            <div className="w-6 h-6 bg-gray-300 rounded animate-pulse"></div>
                        </div>
                    </div>

                    {/* 통계 내용 스켈레톤 */}
                    <div className="p-6 space-y-6">
                        {/* 총 조회수 스켈레톤 (중앙정렬) */}
                        <div className="flex justify-center mb-6">
                            <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 animate-pulse">
                                <div className="text-center">
                                    <div className="w-6 h-6 bg-gray-300 rounded mx-auto mb-3"></div>
                                    <div className="h-4 bg-gray-300 rounded w-20 mx-auto mb-2"></div>
                                    <div className="w-16 h-16 bg-gray-300 rounded-full mx-auto"></div>
                                </div>
                            </div>
                        </div>

                        {/* 스크롤 조회, 상세 조회 스켈레톤 */}
                        <div className="grid grid-cols-2 gap-3 mb-6">
                            {[1, 2].map((index) => (
                                <div key={index} className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 animate-pulse">
                                    <div className="text-center">
                                        <div className="w-4 h-4 bg-gray-300 rounded mx-auto mb-2"></div>
                                        <div className="h-3 bg-gray-300 rounded w-16 mx-auto mb-2"></div>
                                        <div className="w-12 h-12 bg-gray-300 rounded-full mx-auto"></div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* 북마크, 캐스팅 매니저 스켈레톤 */}
                        <div className="grid grid-cols-2 gap-3 mb-6">
                            {[1, 2].map((index) => (
                                <div key={index} className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 animate-pulse">
                                    <div className="text-center">
                                        <div className="w-4 h-4 bg-gray-300 rounded mx-auto mb-2"></div>
                                        <div className="h-3 bg-gray-300 rounded w-16 mx-auto mb-2"></div>
                                        <div className="w-12 h-12 bg-gray-300 rounded-full mx-auto"></div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        {/* 상세 분석 카드 스켈레톤 */}
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
                            {[1, 2].map((index) => (
                                <div key={index} className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 animate-pulse">
                                    <div className="h-5 bg-gray-300 rounded w-24 mb-4"></div>
                                    <div className="space-y-3">
                                        {[1, 2, 3, 4].map((item) => (
                                            <div key={item} className="flex items-center justify-between p-2 bg-gray-100 rounded-lg">
                                                <div className="h-3 bg-gray-300 rounded w-20"></div>
                                                <div className="h-3 bg-gray-300 rounded w-12"></div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    // 에러 상태
    if (error || !statsData) {
        return (
            <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
                <div className="bg-gray-50 rounded-2xl shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
                    <div className="flex items-center justify-center py-20">
                        <div className="text-center">
                            <div className="text-red-500 text-6xl mb-4">⚠️</div>
                            <p className="text-gray-600 mb-4">{error || "데이터를 불러올 수 없습니다."}</p>
                            <button
                                onClick={onClose}
                                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors"
                            >
                                닫기
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    const diaryStats = statsData.stats;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
            <div className="bg-gray-50 rounded-2xl shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
                {/* 헤더 */}
                <div className="sticky top-0 z-10 bg-gray-50 border-b border-gray-200 shadow-sm">
                    <div className="flex items-center justify-between py-4 px-6">
                        <div>
                            <h2 className="text-xl font-bold text-gray-900">{getLanguageValue(PAGE_TYPE.STUDENT, "growthDiary.diaryStats.txtTitle")}</h2>
                            <p className="text-sm text-gray-600">{statsData.title}</p>
                        </div>
                        <button
                            onClick={onClose}
                            className="text-gray-400 hover:text-gray-600 transition-colors"
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                </div>

                {/* 통계 내용 */}
                <div className="p-6 space-y-6">
                    {/* 총 조회수 (중앙정렬) */}
                    <div className="flex justify-center mb-6">
                        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                            <div className="text-center">
                                <div className="flex items-center justify-center mx-auto mb-3">
                                    <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                                    </svg>
                                </div>
                                <h3 className="text-sm font-medium text-gray-600 mb-2">{getLanguageValue(PAGE_TYPE.STUDENT, "growthDiary.diaryStats.txtTotalViews")}</h3>
                                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto">
                                    <p className="text-2xl font-bold text-gray-900">{diaryStats.totalViews.toLocaleString()}</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* 스크롤 조회, 상세 조회 */}
                    <div className="grid grid-cols-2 gap-3 mb-6">
                        {/* 스크롤 조회수 */}
                        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
                            <div className="text-center">
                                <div className="flex items-center justify-center mx-auto mb-2">
                                    <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                    </svg>
                                </div>
                                <h3 className="text-[10px] font-medium text-gray-600 mb-1">{getLanguageValue(PAGE_TYPE.STUDENT, "growthDiary.diaryStats.txtScrollViews")}</h3>
                                <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto">
                                    <p className="text-xl font-bold text-gray-900">{diaryStats.scrollViews}</p>
                                </div>
                            </div>
                        </div>

                        {/* 상세 조회수 */}
                        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
                            <div className="text-center">
                                <div className="flex items-center justify-center mx-auto mb-2">
                                    <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                                    </svg>
                                </div>
                                <h3 className="text-[10px] font-medium text-gray-600 mb-1">{getLanguageValue(PAGE_TYPE.STUDENT, "growthDiary.diaryStats.txtDetailViews")}</h3>
                                <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto">
                                    <p className="text-xl font-bold text-gray-900">{diaryStats.detailViews}</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* 북마크, 캐스팅 매니저 방문 */}
                    <div className="grid grid-cols-2 gap-3 mb-6">
                        {/* 북마크 */}
                        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
                            <div className="text-center">
                                <div className="flex items-center justify-center mx-auto mb-2">
                                    <svg className="w-4 h-4 text-gray-600" fill="currentColor" viewBox="0 0 24 24">
                                        <path d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16l7-3 7 3z" />
                                    </svg>
                                </div>
                                <h3 className="text-[10px] font-medium text-gray-600 mb-1">{getLanguageValue(PAGE_TYPE.STUDENT, "growthDiary.diaryStats.txtBookmarks")}</h3>
                                <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto">
                                    <p className="text-xl font-bold text-gray-900">{diaryStats.totalBookmarks}</p>
                                </div>
                            </div>
                        </div>

                        {/* 캐스팅매니저 방문 */}
                        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
                            <div className="text-center">
                                <div className="flex items-center justify-center mx-auto mb-2">
                                    <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                    </svg>
                                </div>
                                <h3 className="text-[10px] font-medium text-gray-600 mb-1">{getLanguageValue(PAGE_TYPE.STUDENT, "growthDiary.diaryStats.txtCastingManager")}</h3>
                                <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center mx-auto">
                                    <p className="text-xl font-bold text-gray-900">{diaryStats.visitedCastingManagers}</p>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* 상세 분석 카드들 */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
                        {/* 조회수 성장 추이 */}
                        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
                            <h3 className="text-sm font-semibold text-gray-900 mb-4">{getLanguageValue(PAGE_TYPE.STUDENT, "growthDiary.diaryStats.txtViewGrowthTrend")}</h3>
                            <div className="space-y-3">
                                <div className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                                    <span className="text-xs font-medium text-gray-700">{getLanguageValue(PAGE_TYPE.STUDENT, "growthDiary.diaryStats.txtAfter24Hours")}</span>
                                    <span className="text-xs text-gray-900">{diaryStats.viewGrowth.after24h}</span>
                                </div>
                                <div className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                                    <span className="text-xs font-medium text-gray-700">{getLanguageValue(PAGE_TYPE.STUDENT, "growthDiary.diaryStats.txtAfter7Days")}</span>
                                    <span className="text-xs text-gray-900">{diaryStats.viewGrowth.after7d}</span>
                                </div>
                                <div className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                                    <span className="text-xs font-medium text-gray-700">{getLanguageValue(PAGE_TYPE.STUDENT, "growthDiary.diaryStats.txtAfter30Days")}</span>
                                    <span className="text-xs text-gray-900">{diaryStats.viewGrowth.after30d}</span>
                                </div>
                                {/* <div className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                                    <span className="text-xs font-medium text-gray-700">{getLanguageValue(PAGE_TYPE.STUDENT, "growthDiary.diaryStats.txtTopPercent")}</span>
                                    <span className="text-xs text-gray-900">{diaryStats.viewGrowth.percentile}</span>
                                </div> */}
                            </div>
                        </div>

                        {/* 북마크 성장 추이 */}
                        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
                            <h3 className="text-sm font-semibold text-gray-900 mb-4">{getLanguageValue(PAGE_TYPE.STUDENT, "growthDiary.diaryStats.txtBookmarkGrowthTrend")}</h3>
                            <div className="space-y-3">
                                <div className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                                    <span className="text-xs font-medium text-gray-700">{getLanguageValue(PAGE_TYPE.STUDENT, "growthDiary.diaryStats.txtAfter24Hours")}</span>
                                    <span className="text-xs text-gray-900">{diaryStats.bookmarkGrowth.after24h}</span>
                                </div>
                                <div className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                                    <span className="text-xs font-medium text-gray-700">{getLanguageValue(PAGE_TYPE.STUDENT, "growthDiary.diaryStats.txtAfter7Days")}</span>
                                    <span className="text-xs text-gray-900">{diaryStats.bookmarkGrowth.after7d}</span>
                                </div>
                                <div className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                                    <span className="text-xs font-medium text-gray-700">{getLanguageValue(PAGE_TYPE.STUDENT, "growthDiary.diaryStats.txtAfter30Days")}</span>
                                    <span className="text-xs text-gray-900">{diaryStats.bookmarkGrowth.after30d}</span>
                                </div>
                                {/* <div className="flex items-center justify-between p-2 bg-gray-50 rounded-lg">
                                    <span className="text-xs font-medium text-gray-700">{getLanguageValue(PAGE_TYPE.STUDENT, "growthDiary.diaryStats.txtTopPercent")}</span>
                                    <span className="text-xs text-gray-900">{diaryStats.bookmarkGrowth.percentile}</span>
                                </div> */}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DiaryStatsPopup;
